import {
  coerceUploadUrlForDevBrowser,
  isSignedPrivateDownloadUrl,
  signedPrivateDownloadUrlStillValid,
} from './referenceImageUrl'

/**
 * 判断 URL 是否已是本地 /uploads/ 或同源站内路径。
 * 本地路径跳过任何处理——multipart 上传已直接存本地，不需要再检测或转存。
 */
function isLocalReferenceUrl(raw: string): boolean {
  const trimmed = raw.trim()
  if (!trimmed) return false
  // 相对 /uploads/ 路径
  if (trimmed.startsWith('/uploads/') || trimmed === '/uploads') return true
  // 拼好的绝对 /uploads/ URL（开发态或生产态）
  try {
    const u = new URL(trimmed)
    if (u.pathname.startsWith('/uploads/') || u.pathname === '/uploads') return true
  } catch {
    /* ignore */
  }
  return false
}

export type EnsureReferenceResult = {
  /** 全部通过检测后的 URL 数组，顺序与输入一致；本地路径在开发态下会 coerce 为相对路径 */
  refs: string[]
  /** 检测过程中任一失败时的错误信息；全部通过则为 null */
  errorMessage: string | null
}

/**
 * 提交生图前的参考图守门检测（纯检测，不做任何转存）。
 *
 * 处理策略：
 * - 本地 /uploads/ 路径 → 放行（AI 平台在 dev 下访问不到是既存问题，此处不转存）
 * - 七牛签名 URL（带 e=token=）→ 检测是否过期
 *   - 未过期 → 放行（公网可达，AI 平台能直接拉取）
 *   - 已过期 → 返回 errorMessage 拦截提交
 * - 其他外链 → 放行（信任调用方；不做可达性检测避免提交时网络抖动）
 *
 * 时间线展示历史 round 时参考图过期 → 前端 <img onerror={ImageLoadFailurePlaceholder}> fallback。
 * 本函数只负责"不让过期的 CDN URL 被提交给 AI 平台"。
 */
export async function ensureReferenceImagesLocal(
  refs: readonly string[],
): Promise<EnsureReferenceResult> {
  if (!refs.length) return { refs: [], errorMessage: null }

  const checked: string[] = []
  for (const url of refs) {
    const trimmed = url.trim()

    // 1. 已是本地 /uploads/ → 开发态 coerce 为相对路径避免跨域
    if (isLocalReferenceUrl(trimmed)) {
      const coerced = coerceUploadUrlForDevBrowser(trimmed)
      checked.push(coerced)
      continue
    }

    // 2. 七牛签名 URL → 检测过期
    if (isSignedPrivateDownloadUrl(trimmed)) {
      if (!signedPrivateDownloadUrlStillValid(trimmed)) {
        return {
          refs: [],
          errorMessage: '参考图已过期（七牛签名 URL 过期），请重新从图库选择',
        }
      }
    }
    // 3. 其他外链 → 放行，原样返回
    checked.push(trimmed)
  }

  return { refs: checked, errorMessage: null }
}
